import React, { useCallback, useEffect, useState } from "react";
import { Tag, Tooltip, Spin } from "antd";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";

import { fetchDefontanaStock } from "../../utils/fetchProducts";

// Shows a single variant's live Defontana stock. Only call this per-variant
// (product detail page) — never in a bulk/list view, each request can
// trigger a full Defontana catalog fetch on cache miss.
const DefontanaStockTag = ({ variantId, tivendoId }) => {
  const [status, setStatus] = useState("idle"); // idle | loading | success | no-sku | not-found | error
  const [stock, setStock] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStock = useCallback(async () => {
    if (!tivendoId) {
      setStatus("no-sku");
      return;
    }
    setStatus("loading");
    try {
      const result = await fetchDefontanaStock(variantId);
      setStock(result.stock);
      setStatus("success");
    } catch (err) {
      const httpStatus = err?.response?.status;
      const message = err?.response?.data?.message;
      if (httpStatus === 400) {
        setStatus("no-sku");
      } else if (httpStatus === 404) {
        setStatus("not-found");
        setErrorMessage(message || "No se encontró el artículo en Defontana.");
      } else {
        setStatus("error");
        setErrorMessage(message || "No se pudo consultar el stock.");
      }
    }
  }, [variantId, tivendoId]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  if (status === "idle" || status === "loading") {
    return (
      <Tag icon={<Spin indicator={<LoadingOutlined spin />} size="small" />} color="default">
        Defontana...
      </Tag>
    );
  }

  if (status === "no-sku") {
    return (
      <Tooltip title="Configura un SKU (Tivendo ID) para ver el stock Defontana.">
        <Tag color="default">Defontana: sin SKU</Tag>
      </Tooltip>
    );
  }

  if (status === "not-found") {
    return (
      <Tooltip title={errorMessage}>
        <Tag color="orange">
          Defontana: sin coincidencia <ReloadOutlined onClick={loadStock} className="ml-1 cursor-pointer" />
        </Tag>
      </Tooltip>
    );
  }

  if (status === "error") {
    return (
      <Tooltip title={errorMessage}>
        <Tag color="red">
          Defontana: error <ReloadOutlined onClick={loadStock} className="ml-1 cursor-pointer" />
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Stock de Defontana (bodega mundotito), con hasta 60s de rezago.">
      <Tag color={stock > 0 ? "green" : "gold"}>
        Defontana: {stock} <ReloadOutlined onClick={loadStock} className="ml-1 cursor-pointer" />
      </Tag>
    </Tooltip>
  );
};

export default DefontanaStockTag;
